import React, { MouseEvent } from 'react';
import styled from 'styled-components';

import Divider from '@/components/atom/Divider';
import MaterialIcon from '@/components/atom/MaterialIcon';
import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import DefaultDoctorProfile from '@/components/atom/icon/DefaultDoctorProfile';
import Dot from '@/components/atom/icon/Dot';
import DotMenu from '@/components/atom/icon/DotMenu';
import { LineClampTextStyled } from '@/components/atom/text/DefaultText';
import ClickLogger from '@/components/common/ClickLogger';
import LinkTag from '@/components/common/link-tag';
import LazyImage from '@/components/molecules/LazyImage';
import NurseProfile from '@/components/molecules/nurse-profile';
import BlockQuestion from '@/components/molecules/question-list/question-item/block-question';

import { FAQ_LOG_EVENT } from '@/constants/logs/faq';
import { QuestionDetailModalDataType } from '@/hooks/qna/useQuestionDetailViewModel';
import { REPORT_TYPE, ROLE_TYPE } from '@/hooks/useReportModal';
import ContentMarkDown from '@/shared/ui/content-mark-down/ContentMarkDown';
import theme from '@/styles/theme';
import { AnswerLikeType } from '@/types/bff/gql-schema';
import { Nullable } from '@/types/common';
import { AnswererType, QnaDetailAnswerVo } from '@/types/qna';
import { absoluteTime, getAnswerDetail, getQndAnswererMedicalStaffType } from '@/utils/format';
import { cn } from '@/utils/style';
import AnswerFootnote from './AnswerFootnote';
import AnswerReview from './AnswerReview';
import AnswerWriteReview from './AnswerWriteReview';
import AnswererHospital from './AnswererHospital';

export interface AnswerDetailProps {
  answer: QnaDetailAnswerVo;
  categoryName: string;
  onShowModal: (e: MouseEvent<HTMLButtonElement>, data: QuestionDetailModalDataType) => void;
  onClickWriteReview: (answerPid: string) => void;
  onClickLikeAnswer: (answerPid: string, type: AnswerLikeType, myLikeType: Nullable<AnswerLikeType>) => void;
  onClickMagazine: (magazinePid: string) => void;
  moveToHospitalDetail: (hospitalPid: string) => void;
  moveToDoctorDetailPage: (answererType: AnswererType, doctorId?: string | null) => void;
  onClickOuterLink: (url: string) => void;
  questionPid: string;
  extractTextFromChildren: (children: React.ReactNode) => string;
  isAiAnswerChecked?: boolean;
  advertisementComponent: React.ReactNode;
}

const AnswerDetail = ({
  answer,
  isAiAnswerChecked,
  categoryName,
  onShowModal,
  onClickLikeAnswer,
  onClickMagazine,
  onClickWriteReview,
  moveToHospitalDetail,
  moveToDoctorDetailPage,
  onClickOuterLink,
  questionPid,
  advertisementComponent,
  extractTextFromChildren,
}: AnswerDetailProps) => {
  return (
    <>
      {/* 답변한 의사/간호사 */}
      <AnswerWrapper>
        <AnswerTop
          onClick={() => {
            moveToDoctorDetailPage(answer.answerer.answererType, answer.answerer.answererPublicId);
          }}
        >
          <AnswerWriterInfo>
            {answer.answerer.answererType === AnswererType.Nurse ? (
              <NurseProfile url={answer.answerer.profileImageUrl} size={48} />
            ) : (
              <LazyImage
                width={48}
                height={48}
                objectFit={'cover'}
                loader={<DefaultDoctorProfile />}
                src={answer.answerer.profileImageUrl}
              />
            )}
            <AnswerWriter>
              <AnswerWriterName>
                {getQndAnswererMedicalStaffType(answer.answerer.answererType, answer.answerer.name)}
              </AnswerWriterName>
              <AnswerWriterHospital>
                <AnswerWriterHospitalName lineNumber={1} isSelectable>
                  {answer.answerer.affiliatedInstitutionName}
                </AnswerWriterHospitalName>
                <Dot size={3} areaWidth={16} color={theme.color.gray300} />
                <span>{answer.createdDate}</span>
              </AnswerWriterHospital>
            </AnswerWriter>
            <Time dateTime={absoluteTime(answer.createdAt)} />
          </AnswerWriterInfo>
          <ReportButton
            type='button'
            onClick={(e) =>
              onShowModal(e, {
                report: {
                  targetId: answer.answerPid,
                  targetType: REPORT_TYPE.ANSWER,
                  roleId: answer.answerer.answererId,
                  role: answer.answerer.answererType === ROLE_TYPE.AI ? ROLE_TYPE.AI : ROLE_TYPE.DOCTOR,
                  isBlocked: answer.isBlockedByMe,
                },
              })
            }
          >
            <DotMenu />
          </ReportButton>
        </AnswerTop>

        {/* 답변 (차단/미차단) */}
        {answer.isBlockedByMe ? (
          <BlockQuestion />
        ) : (
          <AnswerContentWrapper>
            {answer.answerer.answererType !== AnswererType.Doctor ? (
              <MarkdownAnswerContent>
                {answer.answerer.answererType === AnswererType.Ai && (
                  <>
                    <div className={cn('mb-[24px] flex items-start gap-[8px] rounded-[8px] bg-gray-20 p-[12px]')}>
                      <MaterialIcon className='mt-0.5' name={'error'} color={theme.color.yellow700} size={20} filled />
                      <p className={cn('typography-ui-15r !m-0 break-keep text-gray-600')}>
                        AI가 데이터를 기반으로 생성한 답변으로, 어떠한 의학적 행위로 해석될 수 없습니다.
                      </p>
                    </div>
                    <p
                      className={'whitespace-pre-line'}
                    >{`안녕하세요. 실시간 고민 해결사입니다.\n[${categoryName}]에 관한 고민이 있으시군요. 질문에 대해 답변드리겠습니다.`}</p>
                  </>
                )}
                <ContentMarkDown
                  markdown={getAnswerDetail(answer)}
                  components={{
                    del: ({ children }) => <>~{children}~</>,
                    a: (node) => {
                      return (
                        <ClickLogger
                          params={{
                            eventName: FAQ_LOG_EVENT.CLICK_OUTER_LINK,
                            payload: {
                              questionPid: questionPid ?? '',
                              linkUrl: node.href ?? '',
                              linkText: extractTextFromChildren(node.children),
                            },
                          }}
                        >
                          <LinkTag
                            href={node.href ?? ''}
                            onClickNavigateToScreen={() => onClickOuterLink(node.href ?? '')}
                            target='_blank'
                            className='qna-link'
                            wrapperStyle={{
                              display: 'inline-block',
                            }}
                          >
                            {node.children}
                          </LinkTag>
                        </ClickLogger>
                      );
                    },
                  }}
                />
                {answer.answerer.answererType === AnswererType.Ai && (
                  <>
                    <p>위 내용은 닥터나우가 의료진과 함께 개발한, 건강 정보에 최적화된 AI 답변입니다.</p>
                    <p>도움이 필요하실 때 언제든지 다시 이용해 주세요.</p>
                  </>
                )}
              </MarkdownAnswerContent>
            ) : (
              <TextAnswerContent>{getAnswerDetail(answer)}</TextAnswerContent>
            )}

            {/* AI 답변 검수 완료 배지 */}
            {answer.answerer.answererType === AnswererType.Ai && isAiAnswerChecked && (
              <div className={cn('flex w-fit items-center gap-[4px] rounded-[6px] bg-gray-20 p-[6px]')}>
                <MaterialIcon name={'verified'} color={theme.color.primary500} size={20} filled />
                <p className={cn('typography-ui-15m text-gray-500')}>의료진이 검토한 답변이에요</p>
              </div>
            )}

            {answer.recommendedMagazineGraph && (
              <RecommendMagazine>
                <h3>선생님이 추천한 콘텐츠예요</h3>
                <RecommendMagazineWrapper
                  role='button'
                  onClick={() => onClickMagazine(answer.recommendedMagazineGraph?.magazinePid ?? '')}
                >
                  <RecommendMagazineContent>
                    <h4>{answer.recommendedMagazineGraph.title}</h4>
                    <p>{answer.recommendedMagazineGraph.description}</p>
                  </RecommendMagazineContent>
                  <RecommendMagazineImage src={answer.recommendedMagazineGraph.titleImageUrl} />
                </RecommendMagazineWrapper>
              </RecommendMagazine>
            )}

            {/* 답변 작성 의사 병원 정보 */}
            {answer.answerer.answererType === AnswererType.Doctor && !!answer.answerer.affiliation && (
              <AnswererHospital affiliation={answer.answerer.affiliation} moveToHospitalDetail={moveToHospitalDetail} />
            )}

            {advertisementComponent}

            {/* Footnote */}
            <AnswerFootnote answer={answer} />

            {/* NPS (만족해요/아쉬워요) */}
            {answer.isReviewViewable ? (
              answer.hasReview ? (
                <AnswerReviewListWrapper>
                  {answer.reviews.map((review) => (
                    <AnswerReview key={`answer-review-${review.id}`} review={review} />
                  ))}
                </AnswerReviewListWrapper>
              ) : (
                <AnswerWriteReview
                  title={'닥터나우 의료진 답변 어떠셨나요?'}
                  description={'목소리를 듣고 좋은 서비스를 제공해드릴게요.'}
                  onClickWriteReview={() => {
                    onClickWriteReview(answer.answerPid);
                  }}
                />
              )
            ) : (
              <AnswerNps>
                <NpsButton
                  isChecked={answer.myLikeType === AnswerLikeType.Like}
                  onClick={() => onClickLikeAnswer(answer.answerPid, AnswerLikeType.Like, answer.myLikeType)}
                >
                  <MaterialIcon
                    name={'thumb_up'}
                    size={16}
                    filled={answer.myLikeType === AnswerLikeType.Like}
                    color={answer.myLikeType === AnswerLikeType.Like ? theme.color.primary600 : theme.color.gray600}
                  />
                  만족해요
                  <LikeCount $display={!!answer.likeCount}>{answer.likeCount}</LikeCount>
                </NpsButton>

                <NpsButton
                  isChecked={answer.myLikeType === AnswerLikeType.Dislike}
                  onClick={() => onClickLikeAnswer(answer.answerPid, AnswerLikeType.Dislike, answer.myLikeType)}
                >
                  <MaterialIcon
                    name={'thumb_down'}
                    size={16}
                    filled={answer.myLikeType === AnswerLikeType.Dislike}
                    color={answer.myLikeType === AnswerLikeType.Dislike ? theme.color.primary600 : theme.color.gray600}
                  />
                  아쉬워요
                  <LikeCount $display={!!answer.dislikeCount}>{answer.dislikeCount}</LikeCount>
                </NpsButton>
              </AnswerNps>
            )}
          </AnswerContentWrapper>
        )}
      </AnswerWrapper>
      <Divider height={'15px'} color={theme.color.gray30} />
    </>
  );
};

export default AnswerDetail;

const AnswerWrapper = styled.li`
  background: ${({ theme }) => theme.color.white};

  cursor: default;
  &:hover {
    cursor: default;
  }
`;

const AnswerTop = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 24px;
  align-items: center;

  ${({ theme }) => `
    border-bottom: 1px solid ${theme.color.gray30};
  `};
`;

const AnswerWriterInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const AnswerWriter = styled.div`
  display: flex;
  flex-flow: column;
  margin-left: 16px;
  align-items: flex-start;
`;

const AnswerWriterName = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI16SB};
    color: ${theme.color.gray700};
  `};
`;

const AnswerWriterHospital = styled.div`
  display: flex;
  flex: 1 1;
  align-items: center;

  ${({ theme }) => `
    color: ${theme.color.gray500};
    span {
      ${theme.typography.UI14M};
    }
  `};
`;

const AnswerWriterHospitalName = styled(LineClampTextStyled)`
  flex: 1;
  ${({ theme }) => `
    ${theme.typography.UI14M};
    `};
`;

const Time = styled.time`
  visibility: hidden;
`;

const ReportButton = styled(DefaultButtonStyled)`
  width: 32px;
  height: 32px;
  border-radius: 4px;

  &:active {
    background: ${({ theme }) => theme.color.gray50};
  }
`;

const AnswerContentWrapper = styled.div`
  padding: 24px;
  ${({ theme }) => `
    border-bottom: 1px solid ${theme.color.gray50};
  `};
`;

const TextAnswerContent = styled.div`
  white-space: pre-wrap;
  ${({ theme }) => `
    ${theme.typography.ARTICLE16R};
    color: ${theme.color.gray500};
    `}
`;

const MarkdownAnswerContent = styled.div`
  ${({ theme }) => `
    ${theme.typography.ARTICLE16R};
    color: ${theme.color.gray500};
    `}

  p {
    margin-bottom: 1.2em;
  }

  a, .qna-link {
    color: ${theme.color.blue600};
    font-weight: 500;
  }
    
  ol {
    list-style: decimal;
    margin-bottom: 1.2em;
    li {
      margin-left: 20px;
    }
  }

  ul {
    margin-bottom: 1.2em;
    list-style: circle;
    li {
      margin-left: 20px;
    }
  }
`;

const AnswerNps = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;
`;

const NpsButton = styled(DefaultButtonStyled)<{ isChecked: boolean }>`
  padding: 10px 0;
  box-sizing: border-box;
  font-weight: 600;
  font-size: 14px;
  vertical-align: middle;
  border-radius: 5px;
  ${({ isChecked, theme }) => `
    ${theme.typography.UI14SB};
    color: ${isChecked ? theme.color.primary500 : theme.color.gray600};
    border: 1px solid
      ${isChecked ? theme.color.primary500 : theme.color.gray200};
      `};

  gap: 8px;
`;

const LikeCount = styled.span<{ $display: boolean }>`
  ${({ theme, $display }) => `
      display: ${$display ? 'block' : 'none'};
      ${theme.typography.UI14SB};
      `};
`;

const RecommendMagazine = styled.div`
  margin-top: 24px;
  width: 100%;
  display: flex;
  flex-direction: column;

  h3 {
    ${({ theme }) => theme.typography.UI16SB};
    color: ${({ theme }) => theme.color.gray700};
    margin-bottom: 12px;
  }
`;

const RecommendMagazineWrapper = styled.section`
  display: flex;
  border: 1px solid ${({ theme }) => theme.color.gray100};
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  width: 100%;
  max-height: 130px;
`;

const RecommendMagazineContent = styled.div`
  padding: 18px 16px;
  flex: 1;

  & > h4 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    margin: 0 0 4px 0;
    width: 100%;
    ${({ theme }) => `
      ${theme.typography.UI14SB};
      color: ${theme.color.primary500};
      `};
  }

  & > p {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    ${({ theme }) => `
      ${theme.typography.UI12M}
      color: ${theme.color.gray500};
      `};
  }
`;

const RecommendMagazineImage = styled.img`
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const AnswerReviewListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
